import { requireSupabase } from "./supabaseClient";

export const DOCUMENT_BUCKET = "technical-documents";
export const MAX_DOCUMENT_BYTES = 50 * 1024 * 1024;

export type DocumentRow = {
  id: string;
  plant_id: string;
  line_id: string | null;
  asset_id: string | null;
  part_id: string | null;
  document_type: string;
  title: string;
  file_name: string | null;
  storage_path: string | null;
  external_url: string | null;
  revision: string | null;
  notes: string | null;
  uploaded_by: string | null;
  created_at: string;
  updated_at: string;
};

export type DocumentTarget =
  | { kind: "asset"; id: string; plantId: string; lineId: string | null; label: string }
  | { kind: "part"; id: string; plantId: string; lineId: null; label: string };

export type RegisterDocumentInput = {
  target: DocumentTarget;
  documentType: string;
  title: string;
  revision?: string;
  notes?: string;
  file: File;
};

function safeFileName(name: string) {
  const cleaned = name.normalize("NFKD").replace(/[^a-zA-Z0-9._-]+/g, "_").replace(/_+/g, "_");
  return cleaned.slice(-150) || "document";
}

function buildStoragePath(target: DocumentTarget, fileName: string) {
  const targetFolder = target.kind === "asset" ? `assets/${target.id}` : `parts/${target.id}`;
  return `plants/${target.plantId}/${targetFolder}/${Date.now()}_${crypto.randomUUID()}_${safeFileName(fileName)}`;
}

function validatedHttpUrl(value: string) {
  let url: URL;
  try { url = new URL(value); }
  catch { throw new Error("Enter a valid external URL."); }
  if (url.protocol !== "https:" && url.protocol !== "http:") throw new Error("External document links must use http or https.");
  return url.toString();
}

export async function loadDocumentTargets(): Promise<DocumentTarget[]> {
  const client = requireSupabase();
  const [assetsRes, partsRes] = await Promise.all([
    client.from("assets").select("id,plant_id,line_id,asset_tag,name,status").neq("status", "retired").order("asset_tag"),
    client.from("parts").select("id,plant_id,internal_part_number,oem_part_number,description").order("description"),
  ]);
  const error = assetsRes.error ?? partsRes.error;
  if (error) throw error;

  const assetTargets: DocumentTarget[] = (assetsRes.data ?? []).map((row) => ({
    kind: "asset" as const,
    id: row.id,
    plantId: row.plant_id,
    lineId: row.line_id,
    label: `${row.asset_tag} · ${row.name}`,
  }));
  const partTargets: DocumentTarget[] = (partsRes.data ?? []).map((row) => ({
    kind: "part" as const,
    id: row.id,
    plantId: row.plant_id,
    lineId: null,
    label: `${row.internal_part_number || row.oem_part_number || "NO PN"} · ${row.description}`,
  }));
  return [...assetTargets, ...partTargets];
}

export async function loadDocuments(): Promise<DocumentRow[]> {
  const client = requireSupabase();
  const { data, error } = await client.from("documents").select("*").order("created_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as DocumentRow[];
}

export async function uploadPrivateDocument(input: RegisterDocumentInput) {
  if (input.file.size > MAX_DOCUMENT_BYTES) throw new Error("File exceeds the 50 MB document limit.");
  const client = requireSupabase();
  const path = buildStoragePath(input.target, input.file.name);

  const { error: uploadError } = await client.storage.from(DOCUMENT_BUCKET).upload(path, input.file, {
    cacheControl: "3600",
    upsert: false,
    contentType: input.file.type || undefined,
  });
  if (uploadError) throw uploadError;

  const { data, error } = await client.rpc("register_document", {
    p_plant_id: input.target.plantId,
    p_line_id: input.target.lineId,
    p_asset_id: input.target.kind === "asset" ? input.target.id : null,
    p_part_id: input.target.kind === "part" ? input.target.id : null,
    p_document_type: input.documentType,
    p_title: input.title,
    p_file_name: input.file.name,
    p_storage_path: path,
    p_external_url: null,
    p_revision: input.revision || null,
    p_notes: input.notes || null,
  });

  if (error) {
    await client.storage.from(DOCUMENT_BUCKET).remove([path]);
    throw error;
  }
  return data as string;
}

export async function registerExternalDocument(input: {
  target: DocumentTarget;
  documentType: string;
  title: string;
  url: string;
  revision?: string;
  notes?: string;
}) {
  const client = requireSupabase();
  const externalUrl = validatedHttpUrl(input.url);
  const { data, error } = await client.rpc("register_document", {
    p_plant_id: input.target.plantId,
    p_line_id: input.target.lineId,
    p_asset_id: input.target.kind === "asset" ? input.target.id : null,
    p_part_id: input.target.kind === "part" ? input.target.id : null,
    p_document_type: input.documentType,
    p_title: input.title,
    p_file_name: null,
    p_storage_path: null,
    p_external_url: externalUrl,
    p_revision: input.revision || null,
    p_notes: input.notes || null,
  });
  if (error) throw error;
  return data as string;
}

export async function updateDocumentMetadata(documentId: string, input: { documentType: string; title: string; revision?: string; notes?: string }) {
  const client = requireSupabase();
  const { error } = await client.rpc("update_document_metadata", {
    p_document_id: documentId,
    p_document_type: input.documentType,
    p_title: input.title,
    p_revision: input.revision || null,
    p_notes: input.notes || null,
  });
  if (error) throw error;
}

export async function openDocument(document: DocumentRow) {
  if (document.external_url) {
    window.open(validatedHttpUrl(document.external_url), "_blank", "noopener,noreferrer");
    return;
  }
  if (!document.storage_path) throw new Error("Document has no stored file path.");
  const client = requireSupabase();
  const { data, error } = await client.storage.from(DOCUMENT_BUCKET).createSignedUrl(document.storage_path, 300);
  if (error) throw error;
  window.open(data.signedUrl, "_blank", "noopener,noreferrer");
}

export async function deletePrivateDocument(document: DocumentRow, reason = "Technical document removed") {
  const client = requireSupabase();
  if (document.storage_path) {
    const { error: storageError } = await client.storage.from(DOCUMENT_BUCKET).remove([document.storage_path]);
    if (storageError) throw storageError;
  }
  const { error } = await client.rpc("delete_document_record", { p_document_id: document.id, p_reason: reason });
  if (error) throw error;
}

export async function currentDocumentRole() {
  const client = requireSupabase();
  const { data: authData } = await client.auth.getUser();
  if (!authData.user) return "viewer";
  const { data, error } = await client.from("profiles").select("role").eq("user_id", authData.user.id).maybeSingle();
  if (error) throw error;
  return data?.role ?? "viewer";
}
